import Phaser from 'phaser';

/**
 * Bildschirmaufteilung, fuer Querformat und Hochformat.
 *
 * Bis hierher las jede Szene `scale.width` und `scale.height` einmal in
 * `create()` und rechnete dann mit festen Pixeln. Auf einem Monitor ging das;
 * auf einem Telefon im Hochformat standen zwei Spalten nebeneinander, von
 * denen jede 150 Pixel breit war, und nach einer Drehung des Geraets blieb das
 * alte Layout einfach stehen.
 *
 * Dieses Modul beantwortet die zwei Fragen, die jede Szene stellt — wie viel
 * Platz ist da, und in welcher Form —, und sorgt dafuer, dass sie bei jeder
 * Groessenaenderung neu gestellt werden.
 */

/**
 * Kleinste Trefferflaeche in Pixeln.
 *
 * 44 ist die Empfehlung von Apple und liegt nah an Googles 48: Darunter trifft
 * ein Finger zuverlaessig daneben. Mit der Maus faellt es nicht auf, deshalb
 * gilt es ueberall.
 */
export const MIN_HIT = 44;

/** Unterhalb dieser Breite werden Spalten gestapelt. */
const COMPACT_WIDTH = 700;

/** Unterhalb dieser Hoehe wird mit Luft gespart. */
const SHORT_HEIGHT = 520;

export interface Layout {
  readonly width: number;
  readonly height: number;
  readonly centreX: number;
  readonly centreY: number;
  /** Hoeher als breit. */
  readonly portrait: boolean;
  /** Zu schmal fuer zwei Spalten nebeneinander. */
  readonly compact: boolean;
  /** Zu flach fuer grosszuegige Abstaende. */
  readonly short: boolean;
  /** Rand zum Bildschirm. */
  readonly margin: number;
  /** Breite, die fuer eine Textspalte in der Mitte bleibt. */
  readonly columnWidth: number;
}

export function layoutOf(scene: Phaser.Scene): Layout {
  const { width, height } = scene.scale;
  const compact = width < COMPACT_WIDTH;
  const margin = compact ? 16 : 32;
  return {
    width,
    height,
    centreX: width / 2,
    centreY: height / 2,
    portrait: height > width,
    compact,
    short: height < SHORT_HEIGHT,
    margin,
    columnWidth: Math.min(720, width - margin * 2),
  };
}

/**
 * Laesst eine Szene sich bei jeder Groessenaenderung neu aufbauen.
 *
 * Entprellt: Beim Ziehen am Fensterrand kommen Dutzende Ereignisse je Sekunde,
 * und ein vollstaendiger Neuaufbau je Ereignis liesse das Fenster ruckeln.
 * Beim Drehen eines Telefons kommen oft zwei kurz hintereinander, das erste
 * noch mit den alten Massen.
 *
 * Die Anmeldung loest sich mit der Szene: Phaser verwendet Szenen-Instanzen
 * wieder, und ein Horcher, der den Szenenwechsel ueberlebt, zeichnete eine
 * Szene neu, die gar nicht mehr laeuft.
 */
export function watchResize(scene: Phaser.Scene, redraw: () => void, delayMs = 120): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let last = `${scene.scale.width}x${scene.scale.height}`;

  const onResize = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const now = `${scene.scale.width}x${scene.scale.height}`;
      if (now === last || !scene.sys.isActive()) return;
      last = now;
      redraw();
    }, delayMs);
  };

  scene.scale.on(Phaser.Scale.Events.RESIZE, onResize);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    if (timer !== null) clearTimeout(timer);
    scene.scale.off(Phaser.Scale.Events.RESIZE, onResize);
  });
}

/**
 * Macht ein kleines Textobjekt mit dem Finger treffbar.
 *
 * Die Beschriftung bleibt, wie sie ist; nur die unsichtbare Trefferflaeche
 * waechst auf mindestens `MIN_HIT` in beide Richtungen, zentriert auf den
 * Text. Gedacht fuer Umschalter und Verweise, die nur aus einem Wort bestehen.
 */
export function makeTappable(
  object: Phaser.GameObjects.Text,
  options: { cursor?: boolean } = {},
): Phaser.GameObjects.Text {
  const w = Math.max(object.width, MIN_HIT);
  const h = Math.max(object.height, MIN_HIT);
  object.setInteractive(
    new Phaser.Geom.Rectangle((object.width - w) / 2, (object.height - h) / 2, w, h),
    Phaser.Geom.Rectangle.Contains,
  );
  if (options.cursor !== false && object.input !== null) object.input.cursor = 'pointer';
  return object;
}

/** Hat das Geraet einen Touchscreen als *hauptsaechliche* Eingabe? */
export function isTouchPrimary(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}
